/**
 * IncentiveApprovalDialog — used by the Universal Approval Requests bell
 * (EditAccessRequest.jsx). When an approver / reviewer / payment-desk user
 * clicks an incentive-config request, the FULL native detail view is shown
 * with in-modal action buttons that match the module's own workflow:
 *
 *   manager_review  →  assigned reviewer / Manager-for-Incentive / MA
 *                      Approve (mandatory reason) / Reject (mandatory reason)
 *   admin_approval  →  Master Admin only
 *                      Approve (optional adjust % ) / Reject
 *   payment_pending →  payment-desk users + MA
 *                      Mark Paid form (Payment Mode / Reference /
 *                      Disbursed On / Remarks)
 */
import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  Award, CheckCircle2, XCircle, Wallet, Users, Percent, HandCoins,
  Clock, ClipboardCheck, Calculator,
} from "lucide-react";

const INR = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STAGE_LABEL = {
  manager_review:  { label: "Pending Reviewer",    cls: "bg-slate-100 text-slate-700 border-slate-300" },
  admin_approval:  { label: "Pending Master Admin", cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  payment_pending: { label: "Pending Disbursement", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  paid:            { label: "Paid",                 cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  rejected:        { label: "Rejected",             cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

function fmtDateTime(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
}

export default function IncentiveApprovalDialog({
  open,
  onClose,
  submissionId,
  onActionDone,
}) {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [payMode, setPayMode] = useState(false);
  const [reason, setReason] = useState("");
  const [adjustPct, setAdjustPct] = useState("");
  const [payForm, setPayForm] = useState({
    payment_mode: "",
    payment_reference: "",
    payment_disbursed_on: new Date().toISOString().slice(0, 10),
    payment_remarks: "",
  });

  useEffect(() => {
    if (!open || !submissionId) return;
    let cancelled = false;
    setLoading(true);
    setSub(null);
    setRejectMode(false);
    setPayMode(false);
    setReason("");
    setAdjustPct("");
    setPayForm({
      payment_mode: "",
      payment_reference: "",
      payment_disbursed_on: new Date().toISOString().slice(0, 10),
      payment_remarks: "",
    });
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get(`/incentive/submissions/${submissionId}`),
          api.get("/incentive/config"),
        ]);
        if (!cancelled) { setSub(s.data); setCfg(c.data); setAdjustPct(String(s.data?.incentive_given_percent ?? "")); }
      } catch (e) {
        if (!cancelled) {
          toast.error(formatError(e.response?.data?.detail) || "Failed to load incentive request");
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, submissionId, onClose]);

  const uid = user?.id;
  const isMaster = user?.role === "master_admin";
  const stage = sub?.stage;
  const tabAccess = cfg?.tab_access || {};
  const miIds = new Set(tabAccess.manager_incentive || []);
  const paymentIds = new Set([
    ...(tabAccess.payment || []),
    ...(cfg?.payment_desk_user_ids || []),
  ]);
  const isMI = uid && miIds.has(uid);
  const isPaymentDesk = uid && paymentIds.has(uid);
  const isSubmitter = sub && sub.created_by_id === uid;

  // Can act at manager_review stage
  const canManagerReview = sub
    && stage === "manager_review"
    && !isSubmitter
    && (isMaster || isMI || sub.assigned_manager_id === uid);
  // Can act at admin_approval stage
  const canAdminApprove = sub && stage === "admin_approval" && isMaster;
  // Can mark paid at payment_pending
  const canMarkPaid = sub && stage === "payment_pending" && (isMaster || isPaymentDesk);

  const canAct = canManagerReview || canAdminApprove || canMarkPaid;

  const doManagerDecision = async (decision) => {
    const r = reason.trim();
    if (r.length < 3) { toast.error("Reason (min 3 chars) is required"); return; }
    setBusy(true);
    try {
      await api.post(`/incentive/submissions/${sub.id}/manager-decision`, {
        decision,
        reason: r,
      });
      toast.success(`Reviewer ${decision}`);
      onActionDone?.(decision);
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  const doAdminDecision = async (decision) => {
    const r = reason.trim();
    if (r.length < 3) { toast.error("Reason (min 3 chars) is required"); return; }
    setBusy(true);
    try {
      const payload = { decision, reason: r };
      if (decision === "approved" && adjustPct !== "" && !isNaN(Number(adjustPct))) {
        payload.incentive_given_percent = Number(adjustPct);
      }
      await api.post(`/incentive/submissions/${sub.id}/admin-decision`, payload);
      toast.success(`Master Admin ${decision}`);
      onActionDone?.(decision);
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  const doMarkPaid = async () => {
    if (!payForm.payment_mode.trim()) { toast.error("Payment Mode is required"); return; }
    if (!payForm.payment_reference.trim()) { toast.error("Payment Reference is required"); return; }
    if (!payForm.payment_disbursed_on) { toast.error("Disbursed On date is required"); return; }
    setBusy(true);
    try {
      await api.post(`/incentive/submissions/${sub.id}/mark-paid`, payForm);
      toast.success("Marked as Paid");
      onActionDone?.("paid");
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  const stageMeta = STAGE_LABEL[stage] || { label: stage, cls: "bg-slate-100 text-slate-700 border-slate-300" };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="incentive-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600" />
            {sub?.client_name || (loading ? "Loading…" : "Incentive Request")}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-slate-400 text-sm">Loading incentive request…</div>
        )}

        {!loading && sub && (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={`${stageMeta.cls} border`}>{stageMeta.label}</Badge>
              {sub.mode && (
                <Badge className="bg-slate-100 text-slate-700 border-slate-300 uppercase text-[10px]">
                  {sub.mode} mode
                </Badge>
              )}
              {sub.order_no && (
                <Badge className="bg-blue-50 text-blue-800 border-blue-200">
                  Order · {sub.order_no}
                </Badge>
              )}
              {sub.completed_yearly_target && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                  Target Complete
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-[13px]">
              <div><span className="text-slate-400">Client:</span> <strong>{sub.client_name}</strong></div>
              <div><span className="text-slate-400">Submitted by:</span> <strong>{sub.created_by_name || "—"}</strong></div>
              <div><span className="text-slate-400">Submitted at:</span> {fmtDateTime(sub.created_at)}</div>
              {sub.assigned_manager_name && (
                <div><span className="text-slate-400">Assigned reviewer:</span> <strong>{sub.assigned_manager_name}</strong></div>
              )}
              {sub.manager_decided_at && (
                <div className="col-span-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-[12px] font-semibold text-slate-700 flex items-center gap-1.5">
                    <ClipboardCheck className="w-3.5 h-3.5" />
                    Reviewer {sub.manager_decision} · {fmtDateTime(sub.manager_decided_at)}
                    {sub.manager_decided_by_name && <span className="text-slate-500 font-normal">· by {sub.manager_decided_by_name}</span>}
                  </div>
                  {sub.manager_reason && (
                    <div className="text-[12px] text-slate-600 mt-0.5 italic">&ldquo;{sub.manager_reason}&rdquo;</div>
                  )}
                </div>
              )}
              {sub.admin_decided_at && (
                <div className="col-span-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2">
                  <div className="text-[12px] font-semibold text-indigo-800 flex items-center gap-1.5">
                    <ClipboardCheck className="w-3.5 h-3.5" />
                    Master Admin {sub.admin_decision} · {fmtDateTime(sub.admin_decided_at)}
                    {sub.admin_decided_by_name && <span className="text-indigo-600 font-normal">· by {sub.admin_decided_by_name}</span>}
                  </div>
                  {sub.admin_reason && (
                    <div className="text-[12px] text-indigo-700 mt-0.5 italic">&ldquo;{sub.admin_reason}&rdquo;</div>
                  )}
                </div>
              )}
              {sub.stage === "paid" && sub.payment_reference && (
                <div className="col-span-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <div className="text-[12px] font-semibold text-emerald-800 flex items-center gap-1.5">
                    <Wallet className="w-3.5 h-3.5" /> Disbursed via {sub.payment_mode} · Ref {sub.payment_reference}
                  </div>
                  <div className="text-[11.5px] text-emerald-700 mt-0.5">
                    {sub.payment_disbursed_on} · by {sub.payment_disbursed_by_name || "—"}
                    {sub.payment_remarks && <span className="italic ml-1">— {sub.payment_remarks}</span>}
                  </div>
                </div>
              )}
            </div>

            {/* Money block */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[12.5px]">
              <div>
                <div className="text-[10.5px] uppercase text-slate-500 font-bold">Before GST</div>
                <div className="font-semibold">{INR(sub.before_gst_amount)}</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase text-slate-500 font-bold">GST %</div>
                <div className="font-semibold">{sub.gst_percent || 0}%</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase text-slate-500 font-bold">Total Order</div>
                <div className="font-semibold">{INR(sub.total_order_value)}</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase text-slate-500 font-bold">Commission %</div>
                <div className="font-semibold">{sub.commission_percent || 0}% <span className="text-slate-500 font-normal">({sub.commission_type || "—"})</span></div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase text-slate-500 font-bold">Commission Amt</div>
                <div className="font-semibold">{INR(sub.commission_amount)}</div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase text-slate-500 font-bold">Incentive Demanded</div>
                <div className="font-semibold">{sub.incentive_demanded_percent || 0}%</div>
              </div>
              <div className="col-span-2 sm:col-span-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2">
                <HandCoins className="w-4 h-4 text-amber-700" />
                <div>
                  <div className="text-[10.5px] uppercase text-amber-800 font-bold">Payable Incentive</div>
                  <div className="text-[15px] font-extrabold text-amber-900">
                    {INR(sub.incentive_amount)}
                    <span className="text-[11.5px] font-medium text-amber-700 ml-2">({sub.incentive_given_percent || 0}% of commission)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Helpers / share */}
            {((sub.helper_user_names || []).length > 0 || (sub.shared_with || []).length > 0) && (
              <div className="text-[12px] text-slate-600 space-y-1">
                {(sub.helper_user_names || []).length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Users className="w-3.5 h-3.5 text-slate-400" /> Helpers:
                    {sub.helper_user_names.map((n, i) => (
                      <Badge key={i} variant="secondary" className="bg-slate-100 text-slate-700">{n}</Badge>
                    ))}
                  </div>
                )}
                {(sub.shared_with || []).length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Percent className="w-3.5 h-3.5 text-slate-400" /> Shared with:
                    {sub.shared_with.map((s, i) => (
                      <Badge key={i} variant="secondary" className="bg-blue-50 text-blue-800">
                        {s.user_name} · {s.percent}%
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex flex-wrap gap-2">
          {/* Stage: manager_review — reviewer / MI / MA action */}
          {canManagerReview && !rejectMode && (
            <div className="w-full space-y-2">
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Reviewer decision reason <span className="text-rose-500">*</span></Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                placeholder="Provide the reason for approval or rejection"
                data-testid="incentive-approval-manager-reason"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="destructive"
                  onClick={() => doManagerDecision("rejected")}
                  disabled={busy || reason.trim().length < 3}
                  data-testid="incentive-approval-manager-reject"
                >
                  <XCircle className="w-4 h-4 mr-1.5" /> Reject
                </Button>
                <Button
                  onClick={() => doManagerDecision("approved")}
                  disabled={busy || reason.trim().length < 3}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="incentive-approval-manager-approve"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                </Button>
              </div>
            </div>
          )}

          {/* Stage: admin_approval — MA only */}
          {canAdminApprove && (
            <div className="w-full space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                    Adjust Incentive % (optional)
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={adjustPct}
                    onChange={(e) => setAdjustPct(e.target.value)}
                    placeholder="e.g. 50"
                    data-testid="incentive-approval-admin-pct"
                  />
                  <div className="text-[10.5px] text-slate-400 mt-1">
                    Leave as-is to keep the submitted %. Only applies on Approve.
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                    Decision reason <span className="text-rose-500">*</span>
                  </Label>
                  <Textarea
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value.slice(0, 500))}
                    placeholder="Provide the reason for approval or rejection"
                    data-testid="incentive-approval-admin-reason"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="destructive"
                  onClick={() => doAdminDecision("rejected")}
                  disabled={busy || reason.trim().length < 3}
                  data-testid="incentive-approval-admin-reject"
                >
                  <XCircle className="w-4 h-4 mr-1.5" /> Reject
                </Button>
                <Button
                  onClick={() => doAdminDecision("approved")}
                  disabled={busy || reason.trim().length < 3}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="incentive-approval-admin-approve"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                </Button>
              </div>
            </div>
          )}

          {/* Stage: payment_pending — Payment desk / MA */}
          {canMarkPaid && !payMode && (
            <Button
              onClick={() => setPayMode(true)}
              disabled={busy}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="incentive-approval-open-paydesk"
            >
              <Wallet className="w-4 h-4 mr-1.5" /> Fill Disbursement Details
            </Button>
          )}
          {canMarkPaid && payMode && (
            <div className="w-full space-y-2 border-t border-slate-200 pt-3">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-sky-700 flex items-center gap-1">
                <Wallet className="w-3.5 h-3.5" /> Disbursement Details
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] font-semibold">Payment Mode <span className="text-rose-500">*</span></Label>
                  <Input
                    value={payForm.payment_mode}
                    onChange={(e) => setPayForm((f) => ({ ...f, payment_mode: e.target.value }))}
                    placeholder="e.g. NEFT / IMPS / Cheque / Cash"
                    data-testid="incentive-approval-pay-mode"
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold">Reference # <span className="text-rose-500">*</span></Label>
                  <Input
                    value={payForm.payment_reference}
                    onChange={(e) => setPayForm((f) => ({ ...f, payment_reference: e.target.value }))}
                    placeholder="UTR / Cheque no."
                    data-testid="incentive-approval-pay-ref"
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold">Disbursed On <span className="text-rose-500">*</span></Label>
                  <Input
                    type="date"
                    value={payForm.payment_disbursed_on}
                    onChange={(e) => setPayForm((f) => ({ ...f, payment_disbursed_on: e.target.value }))}
                    data-testid="incentive-approval-pay-date"
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-semibold">Remarks</Label>
                  <Input
                    value={payForm.payment_remarks}
                    onChange={(e) => setPayForm((f) => ({ ...f, payment_remarks: e.target.value }))}
                    placeholder="(optional)"
                    data-testid="incentive-approval-pay-remarks"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPayMode(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  onClick={doMarkPaid}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="incentive-approval-pay-submit"
                >
                  <Calculator className="w-4 h-4 mr-1.5" /> Save & Mark Paid
                </Button>
              </div>
            </div>
          )}

          {!canAct && sub && !loading && (
            <div className="text-[11.5px] text-slate-500 italic mr-auto">
              {stage === "paid" || stage === "rejected"
                ? `Request already ${stage.replace(/_/g, " ")} — no action needed.`
                : "You are not authorised to act on this request at its current stage."}
            </div>
          )}

          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
